import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import {
  TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  AlertCircle, AlertTriangle, Watch, Shield, Activity,
} from 'lucide-react';
import { Minus } from 'lucide-react';
import { InfoTooltip } from '@/components/shared';
import {
  READINESS_SCORE_TOOLTIP, COMPONENT_TOOLTIPS,
  INJURY_RISK_TOOLTIP, INJURY_RISK_CONFIDENCE_TOOLTIP,
  INJURY_RISK_FACTORS_TOOLTIP, INJURY_RISK_AMPLIFIERS_TOOLTIP,
  INJURY_SIGNAL_TOOLTIPS, SUBJECTIVE_WELLNESS_TOOLTIP,
} from '@/constants/tooltips';
import { BodyRegionMap } from '@/components/shared/BodyRegionMap';
import InjuryHistoryPanel from './InjuryHistoryPanel';
import {
  Chart as ChartJS,
  RadialLinearScale, CategoryScale, LinearScale,
  PointElement, LineElement, Filler,
  Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Radar, Line } from 'react-chartjs-2';
import { InjuryRiskTimelinePoint } from '@/types';

ChartJS.register(
  RadialLinearScale, CategoryScale, LinearScale,
  PointElement, LineElement, Filler, ChartTooltip, Legend,
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AthleteStatusCardProps {
  athleteId?: number;
  isDark: boolean;
  defaultExpanded?: boolean;
}

type ActiveTab = 'readiness' | 'injury-risk';

// Readiness types
interface ReadinessResponse {
  readiness_score: number;
  tier: string;
  confidence_adjusted_score: number;
  score_range: { lower: number; upper: number; center: number };
  components?: {
    hrv?: {
      score?: number; z_score?: number; composite_score: number;
      raw_value?: number; baseline_mean?: number; unit?: string;
      trend?: number; weekly_trend?: number | null; is_estimated?: boolean;
    };
    rhr?: number | {
      score: number; raw_value?: number; unit?: string;
      trend?: number; weekly_trend?: number | null;
    };
    sleep?: {
      final_score: number; duration_score?: number; consistency_penalty?: number;
      raw_value?: number; avg_duration?: number; unit?: string;
      trend?: number; weekly_trend?: number | null;
    };
    acwr?: {
      score: number; raw_value?: number; unit?: string;
      acute_load?: number; chronic_load?: number; zone?: string;
    } | null;
    wellness?: { composite_score: number; hooper_index?: number } | null;
  };
  device_confidence?: { overall_confidence: number; device_type: string };
  date: string;
  warnings: string[];
}

interface MetricData {
  name: string; rawValue: number; unit: string; score: number;
  trend: number; weeklyTrend?: number | null; available: boolean;
  isEstimated?: boolean;
}

// Injury risk types
interface SignalContribution {
  signal: string; raw_value: number; risk_score: number;
  weighted_contribution: number; direction: string; explanation: string;
}

interface RegionalPainSummary {
  region: string; pain_level: number; pain_type: string;
  is_new_onset: boolean; report_date: string;
  previous_injury?: { days_ago: number; severity: string; is_active: boolean };
}

interface InjuryRiskResponse {
  risk_score: number; tier: string; confidence: number;
  signal_contributions?: SignalContribution[];
  coach_actions: string[]; interaction_penalties: string[];
  data_quality: {
    signals_present: number; signals_total: number; shap_coverage: number;
    subjective_available: boolean; confidence_note: string;
  };
  regional_pain_summary?: RegionalPainSummary[];
  date: string; status?: string; message?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RISK_TIER_CONFIG: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Low Risk', color: 'text-green-500' },
  MODERATE: { label: 'Moderate', color: 'text-yellow-500' },
  ELEVATED: { label: 'Elevated', color: 'text-orange-500' },
  HIGH: { label: 'High Risk', color: 'text-red-500' },
  CRITICAL: { label: 'Critical', color: 'text-red-800' },
};

const RISK_TIER_COLORS: Record<string, string> = {
  LOW: '#22c55e', MODERATE: '#eab308', ELEVATED: '#f97316',
  HIGH: '#ef4444', CRITICAL: '#991b1b',
};

function getSignalRiskColor(riskScore: number): string {
  if (riskScore <= 0.20) return '#22c55e';
  if (riskScore <= 0.40) return '#eab308';
  if (riskScore <= 0.60) return '#f97316';
  if (riskScore <= 0.80) return '#ef4444';
  return '#991b1b';
}

function getRiskScoreColor(score: number): string {
  if (score <= 0.20) return '#22c55e';
  if (score <= 0.40) return '#eab308';
  if (score <= 0.60) return '#f97316';
  if (score <= 0.80) return '#ef4444';
  return '#991b1b';
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const AthleteStatusCard: React.FC<AthleteStatusCardProps> = ({ athleteId, isDark, defaultExpanded = false }) => {
  // Readiness state
  const [readinessData, setReadinessData] = useState<ReadinessResponse | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(true);
  const [readinessError, setReadinessError] = useState<string | null>(null);

  // Injury risk state
  const [riskData, setRiskData] = useState<InjuryRiskResponse | null>(null);
  const [timelineData, setTimelineData] = useState<InjuryRiskTimelinePoint[] | null>(null);
  const [riskLoading, setRiskLoading] = useState(true);

  // UI state
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState<ActiveTab>('readiness');

  // ---- Fetch combined athlete status (readiness + injury risk in one call) ----
  useEffect(() => {
    const run = async () => {
      try {
        setReadinessLoading(true);
        setRiskLoading(true);
        setReadinessError(null);

        const qs = 'include_details=true';
        const url = athleteId
          ? getApiUrl(`/api/athlete-status/${athleteId}/?${qs}`)
          : getApiUrl(`/api/athlete-status/?${qs}`);

        const result = await authenticatedFetchWithRetry<{
          readiness: ReadinessResponse | null;
          injury_risk: InjuryRiskResponse | null;
        }>(url, { method: 'GET' }, 'fetching athlete status', 2);

        if (!result.success) {
          setReadinessError(result.error || 'Failed to fetch athlete status');
          setReadinessLoading(false);
          setRiskLoading(false);
          return;
        }

        const data = result.data;

        // Handle top-level error status (backend returns 200 with status:"error" to avoid CloudFront masking)
        if ((data as any)?.status === 'error') {
          console.error('athlete-status returned error:', (data as any).error);
          setReadinessError((data as any).error || 'Failed to fetch athlete status');
          setReadinessLoading(false);
          setRiskLoading(false);
          return;
        }

        // Handle readiness
        const readiness = data?.readiness ?? null;
        if (readiness && ((readiness as any).status === 'no_data' || (readiness as any).status === 'error')) {
          setReadinessData(null);
        } else {
          setReadinessData(readiness);
        }

        // Handle injury risk
        const risk = data?.injury_risk ?? null;
        if (risk && ((risk as any).status === 'no_data' || (risk as any).status === 'insufficient_data' || (risk as any).status === 'error')) {
          setRiskData(null);
        } else {
          setRiskData(risk);
        }

        setReadinessLoading(false);
        setRiskLoading(false);

        // For self-view (no athleteId), trigger sync if readiness is stale
        if (!athleteId && readiness) {
          const today = new Date().toISOString().split('T')[0];
          if (readiness.date && readiness.date !== today) {
            const token = localStorage.getItem('access');
            if (token) {
              try {
                await fetch(getApiUrl('/api/wearables/sync/'), {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                await new Promise(resolve => setTimeout(resolve, 6000));
                const refreshUrl = athleteId
                  ? getApiUrl(`/api/athlete-status/${athleteId}/?${qs}&refresh=true`)
                  : getApiUrl(`/api/athlete-status/?${qs}&refresh=true`);
                const refreshResult = await authenticatedFetchWithRetry<{
                  readiness: ReadinessResponse | null;
                  injury_risk: InjuryRiskResponse | null;
                }>(refreshUrl, { method: 'GET' }, 'refreshing athlete status', 2);
                if (refreshResult.success && refreshResult.data?.readiness) {
                  setReadinessData(refreshResult.data.readiness);
                }
                if (refreshResult.success && refreshResult.data?.injury_risk) {
                  setRiskData(refreshResult.data.injury_risk);
                }
              } catch { /* sync retry is best-effort */ }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching athlete status:', err);
        setReadinessError('Failed to fetch readiness score');
        setReadinessLoading(false);
        setRiskLoading(false);
      }
    };
    run();
  }, [athleteId]);

  // ---- Fetch timeline (after risk loads) ----
  useEffect(() => {
    if (!riskData || !athleteId) return;
    const run = async () => {
      try {
        const url = getApiUrl(`/api/injury-risk/${athleteId}/timeline/?days=45&days_timeline=28`);
        const result = await authenticatedFetchWithRetry<{ timeline: InjuryRiskTimelinePoint[] }>(
          url, { method: 'GET' }, 'fetching injury risk timeline', 1,
        );
        if (result.success && result.data?.timeline) setTimelineData(result.data.timeline);
      } catch { /* non-critical */ }
    };
    run();
  }, [riskData, athleteId]);

  // ---- Readiness helpers ----
  const getReadinessTier = (score: number) => {
    if (score >= 80) return { label: 'Optimal', color: 'text-green-500' };
    if (score >= 60) return { label: 'Moderate', color: 'text-yellow-400' };
    if (score >= 40) return { label: 'Poor', color: 'text-red-500' };
    return { label: 'Low', color: 'text-red-500' };
  };

  const getReadinessLabel = (score: number) => {
    if (score >= 80) return 'Ready to Train';
    if (score >= 65) return 'Train Smart';
    if (score >= 50) return 'Light Activity';
    return 'Recovery Priority';
  };

  const getMetrics = (): MetricData[] => {
    if (!readinessData?.components) return [];
    const metrics: MetricData[] = [];
    const c = readinessData.components;

    if (c.hrv && c.hrv.composite_score !== undefined) {
      const has = c.hrv.raw_value !== undefined && c.hrv.raw_value !== null && c.hrv.raw_value > 0;
      metrics.push({
        name: 'HRV', rawValue: has ? c.hrv.raw_value! : c.hrv.composite_score,
        unit: has ? (c.hrv.unit || 'ms') : '', score: c.hrv.composite_score,
        trend: c.hrv.trend ?? 0, weeklyTrend: c.hrv.weekly_trend ?? null,
        available: true, isEstimated: c.hrv.is_estimated ?? false,
      });
    }

    if (c.rhr !== undefined && c.rhr !== null) {
      if (typeof c.rhr === 'number') {
        metrics.push({ name: 'RHR', rawValue: c.rhr, unit: '', score: c.rhr, trend: 0, available: true });
      } else {
        const has = c.rhr.raw_value !== undefined && c.rhr.raw_value !== null && c.rhr.raw_value > 0;
        metrics.push({
          name: 'RHR', rawValue: has ? c.rhr.raw_value! : c.rhr.score,
          unit: has ? (c.rhr.unit || 'bpm') : '', score: c.rhr.score,
          trend: c.rhr.trend ?? 0, weeklyTrend: c.rhr.weekly_trend ?? null, available: true,
        });
      }
    }

    if (c.sleep && c.sleep.final_score !== undefined) {
      const has = c.sleep.raw_value !== undefined && c.sleep.raw_value !== null && c.sleep.raw_value > 0;
      metrics.push({
        name: 'Sleep', rawValue: has ? c.sleep.raw_value! : c.sleep.final_score,
        unit: has ? (c.sleep.unit || 'hrs') : '', score: c.sleep.final_score,
        trend: c.sleep.trend ?? 0, weeklyTrend: c.sleep.weekly_trend ?? null, available: true,
      });
    }

    if (c.acwr && c.acwr.score !== undefined) {
      const has = c.acwr.raw_value !== undefined && c.acwr.raw_value !== null;
      metrics.push({
        name: 'ACWR', rawValue: has ? c.acwr.raw_value! : c.acwr.score,
        unit: has ? 'ratio' : '', score: c.acwr.score, trend: 0, available: true,
      });
    }

    if (c.wellness && c.wellness.composite_score) {
      metrics.push({
        name: 'Wellness', rawValue: c.wellness.composite_score, unit: '',
        score: c.wellness.composite_score, trend: 0, available: true,
      });
    }

    return metrics;
  };

  const overallTrend = useMemo(() => {
    const metrics = getMetrics();
    const withValues = metrics.filter(m => m.trend !== 0);
    if (withValues.length === 0) return 0;
    return withValues.reduce((s, m) => s + m.trend, 0) / withValues.length;
  }, [readinessData]);

  const lowestMetric = useMemo(() => {
    const m = getMetrics();
    if (m.length === 0) return null;
    return m.reduce((low, cur) => cur.score < low.score ? cur : low);
  }, [readinessData]);

  const getActionableInsight = (metric: MetricData | null): string => {
    if (!metric) return '';
    switch (metric.name) {
      case 'HRV': return metric.score < 50 ? 'Focus on stress reduction and quality sleep tonight' : 'Consider light recovery activities today';
      case 'RHR': return metric.score < 50 ? 'Your resting heart rate indicates fatigue - prioritize rest' : 'Monitor hydration and avoid high-intensity training';
      case 'Sleep': return metric.score < 50 ? 'Prioritize 8+ hours of sleep and improve sleep hygiene' : 'Try going to bed 30 minutes earlier tonight';
      case 'ACWR': return metric.score < 50 ? 'Training load imbalance detected - reduce intensity' : 'Gradually increase training volume this week';
      case 'Wellness': return metric.score < 50 ? 'Focus on recovery: nutrition, hydration, and rest' : 'Check in on mood and stress levels';
      default: return 'Focus on recovery and monitor your metrics';
    }
  };

  // ---- Injury risk helpers ----
  const riskTierConfig = riskData ? (RISK_TIER_CONFIG[riskData.tier] || RISK_TIER_CONFIG.LOW) : null;
  const riskTierColor = riskData ? (RISK_TIER_COLORS[riskData.tier] || RISK_TIER_COLORS.LOW) : '#22c55e';
  const riskPercent = riskData ? Math.round(riskData.risk_score * 100) : 0;

  const topContributors = useMemo(() => {
    if (!riskData?.signal_contributions) return [];
    return riskData.signal_contributions
      .filter(c => c.direction === 'increasing_risk')
      .sort((a, b) => b.weighted_contribution - a.weighted_contribution)
      .slice(0, 7);
  }, [riskData]);

  // ---- Radar chart ----
  const getRadarChartData = (metrics: MetricData[]) => ({
    labels: metrics.map(m => m.name),
    datasets: [{
      label: 'Score',
      data: metrics.map(m => m.score),
      backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.12)',
      borderColor: isDark ? '#3B82F6' : '#2563EB',
      borderWidth: 2,
      pointBackgroundColor: isDark ? '#60A5FA' : '#3B82F6',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: isDark ? '#60A5FA' : '#3B82F6',
    }],
  });

  const radarOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { color: isDark ? '#374151' : '#9CA3AF' },
        grid: { color: isDark ? '#374151' : '#9CA3AF' },
        pointLabels: { color: isDark ? '#9CA3AF' : '#1F2937', font: { size: 12 } },
        ticks: { color: isDark ? '#6B7280' : '#374151', backdropColor: 'transparent', font: { size: 10 }, stepSize: 20 },
        suggestedMin: 0, suggestedMax: 100,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
        titleColor: isDark ? '#F9FAFB' : '#111827',
        bodyColor: isDark ? '#D1D5DB' : '#374151',
        borderColor: isDark ? '#374151' : '#E5E7EB',
        borderWidth: 1, cornerRadius: 8,
        callbacks: { label: (ctx: any) => ` ${ctx.label}: ${ctx.raw.toFixed(1)}` },
      },
    },
  }), [isDark]);

  // ---- Loading ----
  if (readinessLoading && riskLoading) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3" />
            <p className="cgm-text-secondary text-sm">Loading athlete status...</p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Empty state ----
  if (!readinessData && !readinessLoading && !readinessError && !riskData) {
    return (
      <div className="cgm-metric-card rounded-2xl p-8">
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-500/10">
            <Watch className="w-10 h-10 text-blue-500" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">No Data Yet</h3>
            <p className="theme-text-secondary text-base max-w-md mx-auto leading-relaxed">
              Connect your Whoop or Garmin device to start tracking readiness and injury risk.
            </p>
          </div>
          <Link
            to="/connect-wearable"
            className="inline-flex items-center space-x-2 cgm-button px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
          >
            <Watch className="w-5 h-5" />
            <span>Connect Device</span>
          </Link>
        </div>
      </div>
    );
  }

  // ---- Error state ----
  if (readinessError && !riskData) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center space-y-4">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <div>
              <p className="text-red-500 text-sm font-medium mb-2">{readinessError}</p>
              <Link to="/connect-wearable" className="text-blue-500 hover:text-blue-600 text-sm font-medium underline">
                Check device connection
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const metrics = getMetrics();
  const readinessTier = readinessData ? getReadinessTier(readinessData.readiness_score) : null;

  return (
    <div
      className={`cgm-metric-card rounded-2xl p-6 transition-all duration-300 ease-in-out border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}
    >
      {/* ================================================================ */}
      {/* COLLAPSED HEADER - always visible                                */}
      {/* ================================================================ */}
      <div
        className="flex items-center justify-between mb-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Left: Title + badges */}
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-lg font-bold cgm-text-primary whitespace-nowrap">Athlete Status</h3>

          {/* Readiness badge */}
          {readinessTier && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap ${readinessTier.color}`}
              style={{ backgroundColor: readinessTier.color.includes('green') ? '#22c55e15' : readinessTier.color.includes('yellow') ? '#eab30815' : '#ef444415' }}
            >
              {readinessTier.label}
            </span>
          )}

          {/* Injury risk badge */}
          {riskTierConfig && riskData && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 whitespace-nowrap ${riskTierConfig.color}`}
              style={{ backgroundColor: `${riskTierColor}15` }}
            >
              <Shield className="w-3 h-3 flex-shrink-0" />
              {riskTierConfig.label}
            </span>
          )}
        </div>

        {/* Right: Confidence + chevron */}
        <div className="flex items-center gap-3">
          {riskData && (
            <div className="hidden sm:flex flex-col items-end gap-0.5">
              <div className="text-[11px] font-medium cgm-text-secondary flex items-center gap-0.5">
                Baseline Confidence
                <InfoTooltip
                  title={INJURY_RISK_CONFIDENCE_TOOLTIP.title}
                  body={INJURY_RISK_CONFIDENCE_TOOLTIP.body}
                  isDark={isDark}
                  width="w-72"
                  iconSize={2.5}
                  align="right"
                  ariaLabel="How Baseline Confidence is calculated"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`w-16 h-1.5 rounded-full ${isDark ? 'bg-neutral-700' : 'bg-gray-200'} overflow-hidden`}>
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.round(riskData.confidence * 100)}%`, backgroundColor: riskData.confidence >= 0.7 ? '#3b82f6' : '#f59e0b' }}
                  />
                </div>
                <span className="text-xs font-semibold cgm-text-primary">{Math.round(riskData.confidence * 100)}%</span>
              </div>
            </div>
          )}
          <button
            className="text-gray-400 hover:text-gray-200 transition-colors p-1"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* ALWAYS-VISIBLE: Score + Coach Action                             */}
      {/* ================================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        {/* Readiness score */}
        {readinessData && (
          <div>
            <div className="text-sm cgm-text-secondary mb-1">Readiness</div>
            <div className="flex items-baseline gap-2">
              <div className="text-4xl font-bold cgm-text-primary">{readinessData.readiness_score.toFixed(0)}%</div>
            </div>
            <div className="text-xs cgm-text-secondary mt-1">{getReadinessLabel(readinessData.readiness_score)}</div>
          </div>
        )}

        {/* Injury risk score */}
        {riskData && (
          <div>
            <div className="text-sm cgm-text-secondary mb-1">Injury Risk</div>
            <div className="flex items-baseline gap-2">
              <div className="text-4xl font-bold" style={{ color: riskTierColor }}>{riskPercent}</div>
              <span className={`text-xs font-semibold ${riskTierConfig!.color}`}>{riskTierConfig!.label.toUpperCase()}</span>
            </div>
            {riskData.coach_actions.length > 0 && (
              <div className={`flex items-start gap-2 p-2 mt-2 rounded-lg text-sm ${isDark ? 'bg-neutral-800/50' : 'bg-gray-50'}`}>
                <Activity className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: riskTierColor }} />
                <span className="text-xs cgm-text-primary">{riskData.coach_actions[0]}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lowest metric warning */}
      {lowestMetric && lowestMetric.score < 70 && (
        <div className={`flex items-start gap-3 p-3 rounded-xl mb-4 ${isDark ? 'bg-amber-900/20 border-amber-800/30' : 'bg-amber-50 border-amber-200'} border`}>
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
              Watch: {lowestMetric.name} {lowestMetric.unit ? `(${lowestMetric.rawValue.toFixed(1)}${lowestMetric.unit})` : `(Score: ${lowestMetric.score.toFixed(0)})`}
            </span>
            <span className={`text-xs ${isDark ? 'text-amber-200/80' : 'text-amber-700'}`}>
              {getActionableInsight(lowestMetric)}
            </span>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* EXPANDED CONTENT - Tabs                                          */}
      {/* ================================================================ */}
      <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[5000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        {/* Tab bar */}
        <div className={`flex gap-1 mb-5 p-1 rounded-lg ${isDark ? 'bg-neutral-800/50' : 'bg-gray-100'}`}>
          <TabButton label="Performance Readiness" icon={<TrendingUp className="w-3.5 h-3.5" />}
            active={activeTab === 'readiness'} isDark={isDark}
            onClick={(e) => { e.stopPropagation(); setActiveTab('readiness'); }} />
          <TabButton
            label="Injury Risk" icon={<Shield className="w-3.5 h-3.5" />}
            active={activeTab === 'injury-risk'} isDark={isDark}
            onClick={(e) => { e.stopPropagation(); setActiveTab('injury-risk'); }}
            badge={riskData && riskData.tier !== 'LOW' ? riskTierConfig?.label : undefined}
            badgeColor={riskTierColor}
          />
        </div>

        {/* Tab content */}
        {activeTab === 'readiness' ? (
          <ReadinessTabContent
            readinessData={readinessData}
            metrics={metrics}
            isDark={isDark}
            radarChartData={metrics.length > 0 ? getRadarChartData(metrics) : null}
            radarOptions={radarOptions}
          />
        ) : (
          <InjuryRiskTabContent
            riskData={riskData}
            timelineData={timelineData}
            topContributors={topContributors}
            isDark={isDark}
            athleteId={athleteId}
          />
        )}
      </div>

      {/* Warnings */}
      {readinessData?.warnings && readinessData.warnings.length > 0 && (
        <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-amber-950/40 border-amber-700/50' : 'bg-amber-50 border-amber-300'} border`}>
          <p className={`text-sm font-semibold ${isDark ? 'text-amber-400' : 'text-amber-800'} mb-2 flex items-center gap-2`}>
            <AlertCircle className="w-4 h-4" />Data Warnings
          </p>
          <ul className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-700'} space-y-1 ml-6`}>
            {readinessData.warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={`${isDark ? 'text-amber-400' : 'text-amber-600'} mt-0.5`}>&#8226;</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <p className="text-xs cgm-text-secondary text-center">
          Updated: {new Date(readinessData?.date || riskData?.date || Date.now()).toLocaleDateString()} |
          {isExpanded ? ' Click to collapse' : ' Click to expand details'}
        </p>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Tab Button
// ---------------------------------------------------------------------------

interface TabButtonProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  isDark: boolean;
  onClick: (e: React.MouseEvent) => void;
  badge?: string;
  badgeColor?: string;
}

const TabButton: React.FC<TabButtonProps> = ({ label, icon, active, isDark, onClick, badge, badgeColor }) => (
  <button
    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
      active
        ? (isDark ? 'bg-neutral-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
        : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
    }`}
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
    {badge && (
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${badgeColor}20`, color: badgeColor }}>
        {badge}
      </span>
    )}
  </button>
);


// ---------------------------------------------------------------------------
// Readiness Tab Content
// ---------------------------------------------------------------------------

interface ReadinessTabContentProps {
  readinessData: ReadinessResponse | null;
  metrics: MetricData[];
  isDark: boolean;
  radarChartData: any;
  radarOptions: any;
}

const ReadinessTabContent: React.FC<ReadinessTabContentProps> = ({ readinessData, metrics, isDark, radarChartData, radarOptions }) => {
  if (!readinessData) {
    return <p className="text-sm cgm-text-secondary text-center py-4">No readiness data available.</p>;
  }

  return (
    <div>
      {/* Radar chart (large) */}
      {radarChartData && metrics.length > 0 && (
        <div className={`rounded-xl p-4 border mb-4 ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
          <h4 className="text-sm font-semibold cgm-text-secondary mb-3 flex items-center gap-1.5">
            Readiness Breakdown
            <InfoTooltip title={READINESS_SCORE_TOOLTIP.title} body={READINESS_SCORE_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="How Readiness Score is calculated" />
          </h4>
          <div className="h-56">
            <Radar data={radarChartData} options={radarOptions} />
          </div>
        </div>
      )}

      {/* Component details */}
      {metrics.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold cgm-text-secondary mb-3">Component Details</h4>
          <div className="space-y-2">
            {metrics.map((metric) => {
              const hasRaw = metric.unit && metric.unit.length > 0;
              const displayValue = hasRaw
                ? (metric.name === 'ACWR' ? metric.rawValue.toFixed(2) : metric.rawValue.toFixed(metric.unit === 'bpm' ? 0 : 1))
                : metric.rawValue.toFixed(1);
              const displayUnit = hasRaw ? metric.unit : '';
              const tooltip = COMPONENT_TOOLTIPS[metric.name];

              return (
                <div key={metric.name}
                  className={`flex items-center justify-between p-3 rounded-xl border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs cgm-text-secondary tracking-wide flex items-center gap-1.5">
                      {metric.name}
                      {metric.isEstimated && <span className="text-amber-500 cursor-help" title="Estimated from heart rate data">~</span>}
                      {tooltip && <InfoTooltip title={tooltip.title} body={tooltip.body} isDark={isDark} ariaLabel={`How ${metric.name} is calculated`} />}
                    </span>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className={`text-lg font-bold ${metric.isEstimated ? (isDark ? 'text-amber-400/80' : 'text-amber-600/80') : (isDark ? 'text-white' : 'text-gray-900')}`}>
                        {displayValue}
                      </span>
                      {displayUnit && <span className="text-xs cgm-text-secondary">{displayUnit}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{metric.score.toFixed(0)}/100</span>
                    {metric.trend !== 0 ? (
                      <div className={`flex items-center gap-0.5 ${metric.trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {metric.trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        <span className="text-xs font-medium">{metric.trend > 0 ? '+' : ''}{metric.trend.toFixed(1)}%</span>
                      </div>
                    ) : <Minus className="w-3.5 h-3.5 text-gray-400" />}
                    {metric.weeklyTrend != null && metric.weeklyTrend !== 0 && (
                      <div className={`flex items-center gap-0.5 ${metric.weeklyTrend > 0 ? 'text-green-500/70' : 'text-red-500/70'}`}
                        title={`7-day trend: ${metric.weeklyTrend > 0 ? '+' : ''}${metric.weeklyTrend.toFixed(1)}%`}
                      >
                        <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>7d</span>
                        {metric.weeklyTrend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Device attribution */}
      {readinessData.device_confidence && (
        <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-end">
          <div className="text-xs cgm-text-secondary">
            Data powered by <span className="font-semibold capitalize">{readinessData.device_confidence.device_type === 'whoop' ? 'WHOOP' : readinessData.device_confidence.device_type}</span>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Injury Risk Tab Content
// ---------------------------------------------------------------------------

interface InjuryRiskTabContentProps {
  riskData: InjuryRiskResponse | null;
  timelineData: InjuryRiskTimelinePoint[] | null;
  topContributors: SignalContribution[];
  isDark: boolean;
  athleteId?: number;
}

const InjuryRiskTabContent: React.FC<InjuryRiskTabContentProps> = ({ riskData, timelineData, topContributors, isDark, athleteId }) => {
  if (!riskData) {
    return <p className="text-sm cgm-text-secondary text-center py-4">No injury risk data available.</p>;
  }

  const riskTierColor = RISK_TIER_COLORS[riskData.tier] || RISK_TIER_COLORS.LOW;

  return (
    <div>
      {/* SHAP Waterfall */}
      {topContributors.length > 0 && (
        <RiskFactorAttribution contributions={topContributors} riskScore={riskData.risk_score} isDark={isDark} />
      )}

      {/* Regional Pain Map */}
      {riskData.regional_pain_summary && riskData.regional_pain_summary.length > 0 && (
        <BodyRegionMap data={riskData.regional_pain_summary} isDark={isDark} />
      )}

      {/* Injury History */}
      {athleteId && (
        <InjuryHistoryPanel
          athleteId={athleteId}
          isDark={isDark}
          currentPainByRegion={
            riskData.regional_pain_summary
              ? Object.fromEntries(riskData.regional_pain_summary.map((rp: any) => [rp.region, rp.pain_level]))
              : {}
          }
        />
      )}

      {/* Timeline */}
      {timelineData && timelineData.length > 3 && (
        <MultiSignalTimeline data={timelineData} isDark={isDark} />
      )}

      {/* Additional Coach Actions */}
      {riskData.coach_actions.length > 1 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold cgm-text-secondary mb-2">Recommendations</h4>
          <ul className="space-y-1">
            {riskData.coach_actions.slice(1).map((action, i) => (
              <li key={i} className="flex items-start gap-2 text-sm cgm-text-primary">
                <span className="cgm-text-secondary mt-0.5">-</span>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Interaction Penalties */}
      {riskData.interaction_penalties.length > 0 && (
        <div className={`p-3 rounded-xl mb-4 ${isDark ? 'bg-amber-950/30 border-amber-700/30' : 'bg-amber-50 border-amber-200'} border`}>
          <h4 className={`text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-800'} mb-1 flex items-center gap-1`}>
            <AlertTriangle className="w-3 h-3" />
            Risk Amplifiers
            <InfoTooltip title={INJURY_RISK_AMPLIFIERS_TOOLTIP.title} body={INJURY_RISK_AMPLIFIERS_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="What Risk Amplifiers means" />
          </h4>
          <ul className="space-y-1">
            {riskData.interaction_penalties.map((penalty, i) => (
              <li key={i} className={`text-xs ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>{penalty}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Data Quality */}
      <div className={`flex items-center justify-between text-xs cgm-text-secondary p-2 rounded-lg ${isDark ? 'bg-neutral-800/50' : 'bg-gray-50'}`}>
        <span>{riskData.data_quality.signals_present}/{riskData.data_quality.signals_total} signals</span>
        <span>{riskData.data_quality.subjective_available ? 'Subjective data available' : 'Wearable data only'}</span>
        <span>{riskData.data_quality.confidence_note}</span>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// SHAP Waterfall / Risk Factor Attribution
// ---------------------------------------------------------------------------

const BASE_RISK = 30;

interface RiskFactorAttributionProps {
  contributions: SignalContribution[];
  riskScore: number;
  isDark: boolean;
}

const RiskFactorAttribution: React.FC<RiskFactorAttributionProps> = ({ contributions, riskScore, isDark }) => {
  const finalScore = Math.round(riskScore * 100);
  const totalContribWeight = contributions.reduce((s, c) => s + c.weighted_contribution, 0);

  return (
    <div className="mb-5">
      <h4 className="text-sm font-semibold cgm-text-primary mb-3 flex items-center gap-1.5">
        Risk Factor Attribution (SHAP)
        <InfoTooltip title={INJURY_RISK_FACTORS_TOOLTIP.title} body={INJURY_RISK_FACTORS_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="How each factor contributes" />
      </h4>
      <div className="text-xs cgm-text-secondary mb-3">Signal severity and weighted contribution to overall risk</div>

      <div className="space-y-1.5">
        {contributions.map((contrib) => {
          const severityPct = Math.round(contrib.risk_score * 100);
          const points = totalContribWeight > 0
            ? Math.round((contrib.weighted_contribution / totalContribWeight) * finalScore)
            : 0;
          return (
            <WaterfallRow
              key={contrib.signal}
              label={formatSignalName(contrib.signal)}
              barWidth={Math.max(severityPct, 2)}
              barColor={getSignalRiskColor(contrib.risk_score)}
              valueLabel={formatRawValue(contrib.signal, contrib.raw_value)}
              badgeLabel={`+${points}`}
              isDark={isDark}
              tooltip={INJURY_SIGNAL_TOOLTIPS[contrib.signal]}
            />
          );
        })}
        <div className="pt-1 mt-1 border-t border-gray-200/50 dark:border-neutral-700/50">
          <WaterfallRow label="Final Risk Score" barWidth={finalScore} barColor={getRiskScoreColor(riskScore)} valueLabel={`${finalScore}`} isDark={isDark} isFinal />
        </div>
      </div>
    </div>
  );
};

interface WaterfallRowProps {
  label: string; barWidth: number; barColor: string; valueLabel: string;
  badgeLabel?: string; isDark: boolean; isFinal?: boolean; tooltip?: { title: string; body: string };
}

const WaterfallRow: React.FC<WaterfallRowProps> = ({ label, barWidth, barColor, valueLabel, badgeLabel, isDark, isFinal, tooltip }) => (
  <div className="flex items-center gap-2">
    <div className="w-[145px] flex-shrink-0 flex items-center gap-1">
      <span className={`text-xs truncate ${isFinal ? 'font-bold cgm-text-primary' : 'cgm-text-secondary'}`}>{label}</span>
      {tooltip && <InfoTooltip title={tooltip.title} body={tooltip.body} isDark={isDark} iconSize={3} ariaLabel={`About ${label}`} />}
    </div>
    <div className="flex-1 flex items-center gap-2">
      <div className={`flex-1 h-6 rounded ${isDark ? 'bg-neutral-800' : 'bg-gray-100'} overflow-hidden relative`}>
        <div className="h-full rounded transition-all duration-500 flex items-center"
          style={{ width: `${Math.min(barWidth, 100)}%`, backgroundColor: barColor, minWidth: '4px' }}
        />
      </div>
      <span className="text-xs font-semibold w-[70px] text-right flex-shrink-0" style={{ color: barColor }}>
        {valueLabel}
        {badgeLabel && <span className="text-[10px] font-normal cgm-text-secondary ml-1">{badgeLabel}</span>}
      </span>
    </div>
  </div>
);


// ---------------------------------------------------------------------------
// Multi-Signal Timeline
// ---------------------------------------------------------------------------

interface MultiSignalTimelineProps {
  data: InjuryRiskTimelinePoint[];
  isDark: boolean;
}

const MultiSignalTimeline: React.FC<MultiSignalTimelineProps> = ({ data, isDark }) => {
  const dates = useMemo(() => data.map(d => {
    const dt = new Date(d.date);
    return `${dt.getMonth() + 1}/${dt.getDate()}`;
  }), [data]);

  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? '#9ca3af' : '#6b7280';

  const makeOptions = (_title: string, suggestedMin?: number, suggestedMax?: number) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true, mode: 'index' as const, intersect: false } },
    scales: {
      x: { display: true, grid: { display: false }, ticks: { display: false } },
      y: {
        display: true, grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 9 }, maxTicksLimit: 3 },
        suggestedMin, suggestedMax,
        afterFit: (axis: any) => { axis.width = 40; },
      },
    },
    elements: { point: { radius: 0, hoverRadius: 3 } },
    animation: false as const,
  });

  const riskChartData = {
    labels: dates,
    datasets: [{ label: 'Risk Score (0-100)', data: data.map(d => Math.round(d.risk_score * 100)),
      borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.1)', borderWidth: 2, fill: true, tension: 0.3,
      pointBackgroundColor: data.map(d => getRiskScoreColor(d.risk_score)),
    }],
  };

  const acwrVals = data.map(d => d.acwr_composite).filter(v => v !== null);
  const hasAcwr = acwrVals.length > 3;
  const acwrChartData = {
    labels: dates,
    datasets: [
      { label: 'ACWR', data: data.map(d => d.acwr_composite), borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.1)', borderWidth: 2, fill: true, tension: 0.3 },
      { label: 'Safe Zone Upper', data: data.map(() => 1.3), borderColor: 'rgba(34,197,94,0.3)', borderWidth: 1, borderDash: [4,4], fill: false, pointRadius: 0 },
      { label: 'Safe Zone Lower', data: data.map(() => 0.8), borderColor: 'rgba(34,197,94,0.3)', borderWidth: 1, borderDash: [4,4], fill: '-1' as any, backgroundColor: 'rgba(34,197,94,0.05)', pointRadius: 0 },
    ],
  };

  const hrvVals = data.map(d => d.hrv_rmssd).filter(v => v !== null);
  const hasHrv = hrvVals.length > 3;
  const hrvBaseline = hrvVals.length > 0 ? hrvVals.reduce((s, v) => s + (v || 0), 0) / hrvVals.length : 0;
  const hrvChartData = {
    labels: dates,
    datasets: [
      { label: 'HRV (RMSSD, ms)', data: data.map(d => d.hrv_rmssd), borderColor: '#8b5cf6', backgroundColor: 'rgba(139,92,246,0.1)', borderWidth: 2, fill: true, tension: 0.3 },
      { label: 'Baseline', data: data.map(() => Math.round(hrvBaseline)), borderColor: 'rgba(139,92,246,0.3)', borderWidth: 1, borderDash: [6,3], fill: false, pointRadius: 0 },
    ],
  };

  const subjectiveData = data.map(d => {
    const vals: number[] = [];
    if (d.soreness !== null) vals.push(10 - d.soreness);
    if (d.mental_fatigue !== null) vals.push(10 - d.mental_fatigue);
    if (d.rpe !== null) vals.push(10 - d.rpe);
    if (d.motivation !== null) vals.push(d.motivation);
    return vals.length > 0 ? Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10 : null;
  });
  const hasSubjective = subjectiveData.filter(v => v !== null).length > 3;
  const subjectiveChartData = { labels: dates, datasets: [{ label: 'Subjective Wellness (0-10)', data: subjectiveData, borderColor: '#22c55e', backgroundColor: 'rgba(34,197,94,0.1)', borderWidth: 2, fill: true, tension: 0.3 }] };

  const charts = [
    { title: 'Risk Score (0-100)', data: riskChartData, options: makeOptions('Risk', 0, 100), show: true, color: '#ef4444', tooltip: undefined as { title: string; body: string } | undefined },
    { title: 'ACWR (Acute:Chronic Workload Ratio)', data: acwrChartData, options: makeOptions('ACWR', 0, 2), show: hasAcwr, color: '#3b82f6', tooltip: undefined },
    { title: 'HRV (RMSSD, ms)', data: hrvChartData, options: makeOptions('HRV'), show: hasHrv, color: '#8b5cf6', tooltip: undefined },
    { title: 'Subjective Wellness (0-10)', data: subjectiveChartData, options: makeOptions('Wellness', 0, 10), show: hasSubjective, color: '#22c55e', tooltip: SUBJECTIVE_WELLNESS_TOOLTIP },
  ];

  return (
    <div className="mb-5">
      <h4 className="text-sm font-semibold cgm-text-primary mb-1">Multi-Signal Timeline</h4>
      <p className="text-xs cgm-text-secondary mb-3">28-day view of aligned biometric signals</p>
      <div className="space-y-1">
        {charts.filter(c => c.show).map((chart) => (
          <div key={chart.title}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: chart.color }} />
              <span className="text-[10px] font-medium cgm-text-secondary">{chart.title}</span>
              {chart.tooltip && (
                <InfoTooltip
                  title={chart.tooltip.title}
                  body={chart.tooltip.body}
                  isDark={isDark}
                  iconSize={2.5}
                  ariaLabel={`About ${chart.title}`}
                />
              )}
            </div>
            <div style={{ height: '80px' }}>
              <Line data={chart.data} options={chart.options} />
            </div>
          </div>
        ))}
        <div className="flex justify-between px-[40px]">
          {dates.filter((_, i) => i % Math.max(1, Math.floor(dates.length / 7)) === 0 || i === dates.length - 1).map((d, i) => (
            <span key={i} className="text-[9px] cgm-text-secondary">{d}</span>
          ))}
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSignalName(signal: string): string {
  const names: Record<string, string> = {
    soreness_3d_max: '3-Day Max Soreness', soreness: 'Soreness', mental_fatigue: 'Mental Fatigue',
    rpe: 'RPE', mental_fatigue_slope: 'Mental Fatigue Trend', sleep_hours: 'Sleep Duration', sleep_score: 'Sleep Quality',
    rhr: 'Resting Heart Rate', hrv_rmssd: 'HRV (RMSSD)', hrv_7d_cv: 'HRV Variability',
    hrv_slope: 'HRV Trend', rhr_slope: 'RHR Trend', acwr_composite: 'ACWR',
    session_load_spike_ratio: 'Session Load Spike', load_change_pct_7d: 'Weekly Load Change',
    load_7d_sum: '7-Day Load', consecutive_high_soreness: 'Persistent Soreness',
    rpe_load_divergence: 'RPE-Load Mismatch', resp_rate: 'Respiratory Rate',
    skin_temp: 'Skin Temperature', load_volatility: 'Load Volatility',
    wearable_missing_rate_7d: 'Data Completeness',
    regional_pain_max: 'Regional Pain Max', regional_pain_3d_max: 'Regional Pain 3d Max',
    regional_pain_count: 'Regional Pain Count', regional_asymmetry: 'Regional Asymmetry',
  };
  return names[signal] || signal.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatRawValue(signal: string, value: number): string {
  // Subjective signals: 0 likely means no data was entered (scale is 1-10)
  if (['soreness', 'soreness_3d_max', 'rpe', 'mental_fatigue'].includes(signal)) {
    return value > 0 ? `${value.toFixed(0)}/10` : 'N/A';
  }
  if (signal === 'sleep_hours') return `${value.toFixed(1)} hrs`;
  if (signal === 'hrv_rmssd') return `${value > 0 ? value.toFixed(0) : '0'}ms`;
  if (signal === 'acwr_composite') return value.toFixed(2);
  if (signal === 'rhr') return `${value.toFixed(0)} bpm`;
  if (signal === 'acute_chronic_delta') return `${value > 0 ? '+' : ''}${value.toFixed(0)}`;
  if (signal === 'load_volatility') return value.toFixed(0);
  if (signal === 'resp_rate') return `${value.toFixed(1)} br/m`;
  if (signal === 'skin_temp') return `${value.toFixed(1)}°C`;
  if (signal.includes('pct') || signal.includes('change')) return `${value > 0 ? '+' : ''}${value.toFixed(0)}%`;
  if (signal.includes('spike') || signal.includes('ratio')) return `${(value * 100).toFixed(0)}%`;
  return value.toFixed(1);
}

export default AthleteStatusCard;
